import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";

import { useUserRelations } from "../../../context/UserRelationsContext";
import TempLogo from "../../../images/Temp_Logo.jpg";
import NotificationBell from "../../NotificationBell";
import { SearchBar } from "../../SearchBar";

import styles from "./Navigation.module.css";

type NavigationProps = {
  isLoggedIn: boolean;
  handleSignIn: () => Promise<void>;
  handleSignOut: () => Promise<void>;
  screen_name?: string;
};

export const Navigation = ({
  isLoggedIn,
  handleSignIn,
  handleSignOut,
  screen_name,
}: NavigationProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const { isModerator } = useUserRelations();

  // Close dropdown on click outside - always call this hook
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(e.target as Node)
      ) {
        setProfileMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Don't render on callback page
  if (location.pathname.includes('/callback')) return null;

  // Not logged in: Show just the sign-in button
  if (!isLoggedIn) {
    return (
      <header className={`${styles.stickyHeader} ${styles.notLoggedIn}`}>
        <div className={styles.navContainer}>
          <Link to="/" className={styles.logoSection}>
            <img
              src={TempLogo}
              alt="Free Pattern Warehouse Logo"
              className={styles.logoImg}
            />
            <span className={styles.siteTitle}>Free Pattern Warehouse</span>
          </Link>

          <div className={styles.searchSection}>
            <SearchBar placeholder="Search patterns and users..." />
          </div>

          <div className={styles.rightControls}>
            <button
              className={styles.signInBtn}
              onClick={handleSignIn}
            >
              Log In
            </button>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className={styles.stickyHeader}>
      <div className={styles.navContainer}>
        {/* Logo and title */}
        <Link to="/" className={styles.logoSection}>
          <img
            src={TempLogo}
            alt="Free Pattern Warehouse Logo"
            className={styles.logoImg}
          />
          <span className={styles.siteTitle}>Free Pattern Warehouse</span>
        </Link>

        {/* Search */}
        <div className={styles.searchSection}>
          <SearchBar placeholder="Search patterns and users..." />
        </div>

        {/* Right-side controls */}
        <div className={styles.rightControls}>
          <button
            className={styles.createBtn}
            onClick={() => navigate("/create")}
          >
            Create Pattern
          </button>

          {/* Moderation button for moderators */}
          {isModerator && (
            <button
              className={styles.createBtn}
              onClick={() => navigate("/moderation")}
            >
              Moderation
            </button>
          )}

          {/* Only show notification bell if user has screen_name loaded */}
          {screen_name && (
            <div className={styles.notificationBell}>
              <NotificationBell />
            </div>
          )}

          {/* Profile dropdown - show even during loading */}
          <div className={styles.profileWrapper} ref={profileMenuRef}>
            <button
              className={styles.profileBtn}
              onClick={() => setProfileMenuOpen((o) => !o)}
            >
              <span className={styles.profileName}>
                {screen_name || "Account"}
              </span>
              <svg className={styles.caretIcon} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {profileMenuOpen && screen_name && (
              <div className={styles.profileDropdown}>
                <button
                  className={styles.dropdownItem}
                  onClick={() => { navigate(`/author/${screen_name}`); setProfileMenuOpen(false); }}
                >
                  My Profile
                </button>

                <button
                  className={styles.dropdownItem}
                  onClick={() => { navigate(`/craft-rooms/${screen_name}`); setProfileMenuOpen(false); }}
                >
                  My Craft Room
                </button>
                <button
                  className={styles.dropdownItem}
                  onClick={() => { navigate("/notifications"); setProfileMenuOpen(false); }}
                >
                  Notifications
                </button>
                <button
                  className={styles.dropdownItem}
                  onClick={() => { navigate("/settings"); setProfileMenuOpen(false); }}
                >
                  Settings
                </button>
                <hr className={styles.dropdownDivider} />
                <button
                  className={`${styles.dropdownItem} ${styles.signOutBtn}`}
                  onClick={() => { handleSignOut(); setProfileMenuOpen(false); }}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navigation;